import type { PropAsset } from "building-types/props/PropAsset";
import type { PropCategory } from "building-types/props/propCategoryTypes";
import type { Prop, PropAttributes } from "building-types/props/propTypes";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import {
  SheetGeometryType,
  sheetPolygonGeometryNew,
  sheetPolylineGeometryNew,
  sheetTextGeometryNew,
} from "building-types/views/sheetGeometryTypes";
import { SVG_TO_PDF_SCALE } from "common/constants/pdfSettings";
import { assertIsNotNull } from "common/utils/assertOfType";
import { degreeToRadian } from "common/utils/math";
import { CollisionType, planColliderObstacleNew } from "doc/plans/interiorLabelObstacle";
import type { Obstacles, PlanGenerationContext } from "doc/plans/planGeometrySource";
import { planTextBoxPerimeter, textSizeInView } from "doc/plans/primitives/textPrimitive";
import { mergedPropLibraryNew } from "doc/propTools";
import { aabb2Center, aabb2Move } from "geom/aabb2";
import {
  polyLine2AABB2,
  polyLine2Copy,
  polyLine2Move,
  polyLine2NonUniformScale,
  polyLine2Rotate,
} from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";
import type { IndexAABB2 } from "geom/rtree";
import { defaultPropCache } from "lib/house/propGeometryCache";
import { TAG_FONT_SIZE } from "pdf/planFonts";
import RBush from "rbush";
import { getReportingUnitRoomIds, hostRoomsInReportingUnit } from "../reportingUnitRooms";

const PROP_VISIBLE_HEIGHT = 90;

interface PlanPropProps extends PlanGenerationContext {
  type?: PropCategory;
}

interface SvgPlanPropEntry {
  readonly prop: Prop;
  readonly propAsset: PropAsset;
  readonly propAttributes: PropAttributes;
}

async function getSvgPlanProps({
  building,
  levelId,
  type,
  selectedReportingUnitLabelId,
}: PlanPropProps): Promise<SvgPlanPropEntry[]> {
  const propCatalog = building.settings.props;
  const mergedPropLibrary = mergedPropLibraryNew(building.doc.assets);
  const reportingUnitRoomIds = getReportingUnitRoomIds(building, selectedReportingUnitLabelId);

  const sortedProps: Prop[] = Object.values(building.props);
  sortedProps.sort((a, b) => {
    return a.z - b.z;
  });

  const candidates: { readonly prop: Prop; readonly propAttributes: PropAttributes }[] = [];
  for (const prop of sortedProps) {
    if (prop.room?.level.id !== levelId) {
      continue;
    }

    // Skip props hosted in rooms outside the selected reporting unit.
    if (!hostRoomsInReportingUnit(reportingUnitRoomIds, [prop.room])) {
      continue;
    }

    const propAttributes = propCatalog[prop.src];
    if (!propAttributes) {
      continue;
    }

    const { category } = propAttributes;

    if (type !== undefined && type !== category) {
      continue;
    }

    candidates.push({ prop, propAttributes });
  }

  const entries = await Promise.all(
    candidates.map(async ({ prop, propAttributes }): Promise<SvgPlanPropEntry | null> => {
      const propAsset = await mergedPropLibrary.get(propAttributes.propAssetId);
      if (!propAsset || !propAsset.hasSvg) {
        return null;
      }

      return { prop, propAsset, propAttributes };
    }),
  );

  return entries.filter((entry): entry is SvgPlanPropEntry => entry !== null);
}

async function makeSheetGeometryFromPropLinework(props: PlanPropProps): Promise<SheetGeometry[]> {
  const entries = await getSvgPlanProps(props);
  const geometryByProp = await Promise.all(
    entries.map(async ({ prop, propAsset }): Promise<SheetGeometry[]> => {
      try {
        const parsedLines = await defaultPropCache.getPropPolylines(
          propAsset,
          [prop.x, prop.y],
          degreeToRadian(prop.rotationDegrees),
          prop.scaleX * SVG_TO_PDF_SCALE,
          -prop.scaleY * SVG_TO_PDF_SCALE,
        );

        if (parsedLines.length === 0) {
          return [];
        }

        return [
          sheetPolylineGeometryNew({
            handle: prop.handle ?? prop.room?.roomHandle,
            paths: parsedLines,
            buildingEntity: prop,
            oneObstacle: true,
          }),
        ];
      } catch (e) {
        console.error(e);
        return [];
      }
    }),
  );

  return geometryByProp.flat();
}

async function makeSheetGeometryFromPropFill(props: PlanPropProps): Promise<SheetGeometry[]> {
  const entries = await getSvgPlanProps(props);
  const geometryByProp = await Promise.all(
    entries.map(async ({ prop, propAsset }): Promise<SheetGeometry[]> => {
      try {
        const geometry = await defaultPropCache.getProp2DGeometry(propAsset);
        if (!geometry) {
          return [];
        }

        return geometry.polyLines.map((polygon) => {
          const transformed = polyLine2Copy(polygon);
          polyLine2NonUniformScale(transformed, prop.scaleX * SVG_TO_PDF_SCALE, -prop.scaleY * SVG_TO_PDF_SCALE);
          polyLine2Rotate(transformed, degreeToRadian(prop.rotationDegrees));
          polyLine2Move(transformed, prop.x, prop.y);

          return sheetPolygonGeometryNew({
            handle: prop.handle ?? prop.room?.roomHandle,
            paths: [[transformed]],
            buildingEntity: prop,
          });
        });
      } catch (e) {
        console.error(e);
        return [];
      }
    }),
  );

  return geometryByProp.flat();
}

export async function makeSheetGeometryFromPropFillForAllLevels(props: PlanPropProps): Promise<SheetGeometry[]> {
  // Showroom shows a single level at a time, and PROP_FILL is not in OverridableSheetGeometryCategories
  // so the level-scoping selector in makeBuiltInFloorPlanSelectorsAndFilters is never installed for it.
  // Without this guard, prop fills from upper floors leak onto the visible floor plan as white blobs.
  // Mirrors the pattern in makeSheetGeometryForFloorFill, which is also gated on showroomFloorPlan.
  if (props.renderStyleMode === "showroomFloorPlan") {
    return makeSheetGeometryFromPropFill(props);
  }

  const result: SheetGeometry[] = [];

  for (const levelId in props.building.levels) {
    const levelResult = await makeSheetGeometryFromPropFill({ ...props, levelId });
    result.push(...levelResult);
  }

  return result;
}

export async function makeSheetGeometryFromPropLineworkForAllLevels(props: PlanPropProps): Promise<SheetGeometry[]> {
  const result: SheetGeometry[] = [];

  for (const levelId in props.building.levels) {
    const levelResult = await makeSheetGeometryFromPropLinework({ ...props, levelId });
    result.push(...levelResult);
  }

  return result;
}

async function makeSheetGeometryFromPropTags(props: PlanPropProps): Promise<SheetGeometry[]> {
  const result: SheetGeometry[] = [];

  const propRTree = new RBush<IndexAABB2>();

  const entries = await getSvgPlanProps(props);
  for (const { prop, propAttributes } of entries) {
    const propFloorPlanTag = prop.floorPlanTag ?? propAttributes.floorPlanTag;
    const propFloorPlanTagOffsetX = prop.floorPlanTagOffsetX ?? propAttributes.floorPlanTagOffsetX ?? 0;
    const propFloorPlanTagOffsetY = prop.floorPlanTagOffsetY ?? propAttributes.floorPlanTagOffsetY ?? 0;

    if (propFloorPlanTag && propFloorPlanTag?.length > 0) {
      if (!prop.alignedAABB3) {
        continue;
      }

      const [centerX, centerY] = aabb2Center(prop.alignedAABB3);
      // TODO: move this relaxation into a post-processing step. this will produce inaccurate results with
      // a different font size.
      const [labelSizeX, labelSizeY] = textSizeInView(propFloorPlanTag, TAG_FONT_SIZE);
      const labelPl = polyLine2New([
        centerX - labelSizeX * 0.5,
        centerY - labelSizeY * 0.5,
        centerX + labelSizeX * 0.5,
        centerY - labelSizeY * 0.5,
        centerX + labelSizeX * 0.5,
        centerY + labelSizeY * 0.5,
        centerX - labelSizeX * 0.5,
        centerY + labelSizeY * 0.5,
        centerX - labelSizeX * 0.5,
        centerY - labelSizeY * 0.5,
      ]);

      const aabb = polyLine2AABB2(labelPl);

      const altOffset = labelSizeY * 0.75;

      if (!propFloorPlanTagOffsetX && !propFloorPlanTagOffsetY) {
        // look for collisions with other tags
        const overlap = propRTree.collides(aabb);

        // update the aabb based on the collision result
        if (overlap) {
          aabb2Move(aabb, 0, altOffset);
          polyLine2Move(labelPl, 0, altOffset);
        }
      }

      aabb2Move(aabb, propFloorPlanTagOffsetX, propFloorPlanTagOffsetY);

      polyLine2Move(labelPl, propFloorPlanTagOffsetX, propFloorPlanTagOffsetY);

      const [aabbCenterX, aabbCenterY] = aabb2Center(aabb);
      result.push(
        sheetTextGeometryNew({
          handle: prop.handle ?? prop.room?.docEntityHandle,
          text: propFloorPlanTag,
          x: aabbCenterX,
          y: aabbCenterY,
          buildingEntity: prop,
          rotationBasis: "SHEET",
        }),
      );

      propRTree.insert(aabb as IndexAABB2);
    }
  }

  return result;
}

export async function makeSheetGeometryFromPropTagsForAllLevels(props: PlanPropProps): Promise<SheetGeometry[]> {
  const result: SheetGeometry[] = [];

  for (const levelId in props.building.levels) {
    const levelResult = await makeSheetGeometryFromPropTags({ ...props, levelId });
    result.push(...levelResult);
  }

  return result;
}

/** Returns true if the prop's height relative to its level is at or below the threshold */
export function propHeightPredicate(prop: Prop): boolean {
  assertIsNotNull(prop.room);
  return prop.z - prop.room.level.floorElevation <= PROP_VISIBLE_HEIGHT;
}

export function collectPropLabelObstacles(o: Obstacles, geom: SheetGeometry) {
  if (geom.type === SheetGeometryType.TEXT) {
    const perimeter = planTextBoxPerimeter(geom);
    o.obstacles?.push(planColliderObstacleNew(CollisionType.Props, perimeter));
  }
}
